import { ActivatedRoute, Router } from '@angular/router';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { BasePage } from '../../base.page';
import { Client, Contract } from '../../../domain/data';
import {
    ClientRelation,
    ContractSignatureType,
    CreateContractUseCase,
    CreateContractWithProductsUseCase,
    GetContractUseCase,
    GetFirstHolderUseCase,
    GetSignatoriesUseCase,
    UpdateContractUseCase,
} from '../../../../core';
import { ConfirmContractDialog } from '../../../ui/dialog/confirm-contract/confirm-contract.dialog';
import { ConfirmContractResultDialog } from '../../../ui/dialog/confirm-contract-result/confirm-contract-result.dialog';
import { ContractPage } from '../contract.page';
import { ErrorDialog } from '../../../ui/dialog/error/error.dialog';
import { MainRoutes } from '../../../main-routes.enum';
import { ParentEvents } from '../../../utils/parent-events.service';
import { RecaptchaDirective } from '../../../directives/recaptcha.directive';

import { environment } from '../../../../../environments/environment';

@Component({
    templateUrl: './contract-sign.page.html',
    styleUrls: ['./contract-sign.page.scss'],
})
export class ContractSignPage extends BasePage implements AfterViewInit, OnInit, OnDestroy {
    public ContractSignatureType: typeof ContractSignatureType = ContractSignatureType;
    public ClientRelation: typeof ClientRelation = ClientRelation;

    public contract: Contract;
    public signatories: Client[] = [];
    public signatory: Client;
    public isLegalPerson = false;
    public recaptchaToken: string;
    public isClientPortal = environment.portalClient;

    private backSubscription: Subscription;

    @ViewChild('recaptchaDirective')
    recaptchaDirective: RecaptchaDirective;

    constructor(
        contractPage: ContractPage,
        parentEvents: ParentEvents,
        private createContractUseCase: CreateContractUseCase,
        private createContractWithProductsUseCase: CreateContractWithProductsUseCase,
        private dialog: MatDialog,
        private getContractUseCase: GetContractUseCase,
        private getFirstHolderUseCase: GetFirstHolderUseCase,
        private getSignatoriesUseCase: GetSignatoriesUseCase,
        private router: Router,
        private route: ActivatedRoute,
        private updateContractUseCase: UpdateContractUseCase
    ) {
        super(parentEvents);
        contractPage.showBackButton();
        this.backSubscription = contractPage.back.subscribe(() => {
            window.history.back();
        });
    }

    get canContinue(): boolean {
        return !this.isClientPortal || !!this.recaptchaToken;
    }

    ngOnInit() {
        this.getFirstHolder();
        this.getContract();
        this.getSignatories();
    }

    private getFirstHolder() {
        this.getFirstHolderUseCase.bind().subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.isLegalPerson = transaction.data.isLegalPerson;
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog);
            }
        });
    }

    private getContract() {
        this.getContractUseCase.bind().subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.contract = transaction.data as Contract;
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog);
            }
        });
    }

    private getSignatories() {
        this.getSignatoriesUseCase.bind().subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.signatories = transaction.data as Client[];
                this.signatory = this.signatories[0];
                this.onSignatoryChange();
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog);
            }
        });
    }

    ngOnDestroy() {
        this.backSubscription.unsubscribe();
    }

    onSignatoryChange() {
        this.contract.appropriatenessClientDocId = this.signatory.docId;
    }

    onSubmit() {
        this.updateContractUseCase.bind({ contract: this.contract }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                if (this.isClientPortal) {
                    this.onSubmitPortal();
                } else {
                    this.createContractUseCase.bind().subscribe((transactionCreate) => {
                        if (transactionCreate.isSuccess) {
                            this.router.navigateByUrl(MainRoutes.TEST);
                        } else if (!transactionCreate.isAuthError) {
                            this.dialog.open(ErrorDialog, {
                                data: { text: transactionCreate.error },
                            });
                        }
                    });
                }
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog, {
                    data: { text: transaction.error },
                });
            }
        });
    }

    private onSubmitPortal(): void {
        const dialogRef: any = this.dialog.open(ConfirmContractDialog, {
            data: {
                client: this.signatory,
                isFirstContract: !this.contract.holders.some((participant) => !!participant.encryptedCode),
                only: this.signatories.length === 1,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.recaptchaDirective.refresh().then(() => {
                    this.createContractWithProductsUseCase.bind({ captcha: this.recaptchaToken }).subscribe((transactionCreate) => {
                        if (transactionCreate.isSuccess) {
                            this.dialog.open(ConfirmContractResultDialog, {
                                data: {
                                    client: this.signatory,
                                    success: transactionCreate.data.success,
                                    redirectToPortal: transactionCreate.data.redirect,
                                    isFirstContract: !this.contract.holders.some((participant) => !!participant.encryptedCode),
                                },
                            });
                        } else if (!transactionCreate.isAuthError) {
                            this.dialog.open(ErrorDialog, {
                                data: { text: transactionCreate.error },
                            });
                        }
                    });
                });
            }
        });
    }
}
